import api from "../../services/api";
import type { ComplianceRegister } from "@akfdev-com/shared/src/models/ComplianceRegister";
import type { InspectionRecord } from "@akfdev-com/shared/src/models/InspectionRecord";
import type { Contractor } from "@akfdev-com/shared/src/models/Contractor";
import type { EvidenceFile } from "@akfdev-com/shared/src/models/EvidenceFile";

// Compliance Register CRUD
export const fetchComplianceRegister = async (): Promise<ComplianceRegister[]> => {
  const res = await api.get("/compliance-register");
  return res.data;
};

export const fetchComplianceItem = async (id: string): Promise<ComplianceRegister> => {
  const res = await api.get(`/compliance-register/${id}`);
  return res.data;
};

export const createComplianceItem = async (
  item: Partial<ComplianceRegister>,
): Promise<ComplianceRegister> => {
  const res = await api.post("/compliance-register", item);
  return res.data;
};

export const updateComplianceItem = async (
  id: string,
  item: Partial<ComplianceRegister>,
): Promise<ComplianceRegister> => {
  const res = await api.put(`/compliance-register/${id}`, item);
  return res.data;
};

export const deleteComplianceItem = async (id: string): Promise<void> => {
  await api.delete(`/compliance-register/${id}`);
};

// Inspection Records CRUD
export const fetchInspectionRecords = async (
  complianceItemId?: string,
): Promise<InspectionRecord[]> => {
  const res = await api.get("/inspection-records", {
    params: complianceItemId ? { complianceItemId } : undefined,
  });
  return res.data;
};

export const createInspectionRecord = async (
  record: Partial<InspectionRecord>,
): Promise<InspectionRecord> => {
  const res = await api.post("/inspection-records", record);
  return res.data;
};

export const updateInspectionRecord = async (
  inspectionId: string,
  record: Partial<InspectionRecord>,
): Promise<InspectionRecord> => {
  const res = await api.put(`/inspection-records/${inspectionId}`, record);
  return res.data;
};

export const deleteInspectionRecord = async (inspectionId: string): Promise<void> => {
  await api.delete(`/inspection-records/${inspectionId}`);
};

// Contractor Register CRUD
export const fetchContractors = async (): Promise<Contractor[]> => {
  const res = await api.get("/contractors");
  return res.data;
};

export const createContractor = async (
  contractor: Partial<Contractor>,
): Promise<Contractor> => {
  const res = await api.post("/contractors", contractor);
  return res.data;
};

export const updateContractor = async (
  id: string,
  contractor: Partial<Contractor>,
): Promise<Contractor> => {
  const res = await api.put(`/contractors/${id}`, contractor);
  return res.data;
};

export const deleteContractor = async (id: string): Promise<void> => {
  await api.delete(`/contractors/${id}`);
};

// Evidence Files CRUD + S3 Upload Pipeline
export const fetchEvidenceFiles = async (
  linkedRecordId?: string,
): Promise<EvidenceFile[]> => {
  const res = await api.get("/evidence-files", {
    params: linkedRecordId ? { linkedRecordId } : undefined,
  });
  return res.data;
};

export const createEvidenceFile = async (
  file: Partial<EvidenceFile>,
): Promise<EvidenceFile> => {
  const res = await api.post("/evidence-files", file);
  return res.data;
};

export const deleteEvidenceFile = async (id: string): Promise<void> => {
  await api.delete(`/evidence-files/${id}`);
};

// Evidence Upload Pipeline (S3 presigned URLs)
export interface UploadUrlResponse {
  uploadUrl: string;
  evidenceFileId: string;
  s3Key: string;
  expiresAt: string;
}

export interface DownloadUrlResponse {
  downloadUrl: string;
  fileName: string;
  contentType: string;
}

export const requestUploadUrl = async (params: {
  recordType: 'compliance-register' | 'inspection-record';
  recordId: string;
  fileName: string;
  contentType: string;
}): Promise<UploadUrlResponse> => {
  const res = await api.post("/evidence-files/upload-url", params);
  return res.data;
};

export const confirmUpload = async (evidenceFileId: string): Promise<void> => {
  await api.patch(`/evidence-files/${evidenceFileId}`, { action: 'confirm-upload' });
};

export const getDownloadUrl = async (id: string): Promise<DownloadUrlResponse> => {
  const res = await api.get(`/evidence-files/download-url/${id}`);
  return res.data;
};

// Upload file directly to S3 using presigned URL
export const uploadFileToS3 = async (
  uploadUrl: string,
  file: File,
  contentType: string,
): Promise<void> => {
  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
      'x-amz-server-side-encryption': 'aws:kms',
    },
  });
};

// Notifications
export interface Notification {
  id: string;
  ownerId: string;
  complianceItemId: string;
  complianceItemName: string;
  category: string;
  reminderType: 'warning' | 'alert' | 'critical';
  message: string;
  daysUntilDue?: number;
  daysOverdue?: number;
  nextDueDate: string;
  status: 'unread' | 'read';
  createdAt: string;
}

export const fetchNotifications = async (cursor?: string, pageSize?: number): Promise<{
  items: Notification[];
  nextCursor?: string;
}> => {
  const res = await api.get("/notifications", {
    params: { cursor, pageSize },
  });
  return res.data;
};

export const markNotificationRead = async (id: string): Promise<void> => {
  await api.patch(`/notifications/${id}`, { status: 'read' });
};

// Compliance Reports
export interface ComplianceSummaryReport {
  summary: { total: number; compliant: number; dueSoon: number; overdue: number };
  categories: Array<{ name: string; items: unknown[] }>;
}

export const fetchComplianceSummaryReport = async (format?: 'json' | 'pdf'): Promise<ComplianceSummaryReport | Blob> => {
  if (format === 'pdf') {
    const res = await api.get("/compliance-reports/compliance-summary", {
      params: { format: 'pdf' },
      responseType: 'blob',
    });
    return res.data;
  }
  const res = await api.get("/compliance-reports/compliance-summary", {
    params: { format: 'json' },
  });
  return res.data;
};

export const fetchBuildingSalePack = async (): Promise<unknown> => {
  const res = await api.get("/compliance-reports/building-sale-pack");
  return res.data;
};

export const fetchAnnualDirectorReport = async (
  startDate: string,
  endDate: string,
  format?: 'json' | 'pdf',
): Promise<unknown> => {
  const res = await api.get("/compliance-reports/annual-director", {
    params: { startDate, endDate, format: format || 'json' },
    ...(format === 'pdf' ? { responseType: 'blob' } : {}),
  });
  return res.data;
};

// Audit Trail
export interface AuditEntry {
  ownerId: string;
  userId: string;
  timestamp: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  entityType: string;
  entityId: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  truncated?: boolean;
}

export const fetchAuditTrail = async (params?: {
  entityType?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
  pageSize?: number;
  cursor?: string;
}): Promise<{ items: AuditEntry[]; nextCursor?: string }> => {
  const res = await api.get("/audit-trail", { params });
  return res.data;
};
